import type { SnackbarOrigin } from "@mui/material";
import { enqueueMuiToast, type MuiToastAction, type MuiToastOptions } from "@/contexts/MuiSnackbarProvider";

const TOAST_POSITIONS = {
  "top-left": { vertical: "top", horizontal: "left" },
  "top-center": { vertical: "top", horizontal: "center" },
  "top-right": { vertical: "top", horizontal: "right" },
  "bottom-left": { vertical: "bottom", horizontal: "left" },
  "bottom-center": { vertical: "bottom", horizontal: "center" },
  "bottom-right": { vertical: "bottom", horizontal: "right" }
} as const satisfies Record<string, SnackbarOrigin>;

type ToastPosition = keyof typeof TOAST_POSITIONS;

type ToastOptions = MuiToastOptions & {
  /**
   * Convenience shorthand for `anchorOrigin`.
   * If `anchorOrigin` is provided, it takes precedence over `position`.
   */
  position?: ToastPosition;
};

const resolveAnchorOrigin = (options?: ToastOptions): SnackbarOrigin | undefined => {
  if (!options) {
    return undefined;
  }

  if (options.anchorOrigin) {
    return options.anchorOrigin;
  }

  if (!options.position) {
    return undefined;
  }

  return TOAST_POSITIONS[options.position];
};

const toast = (message: string, options?: ToastOptions) => {
  const anchorOrigin = resolveAnchorOrigin(options);
  const { position: toastPosition, ...rest } = options ?? {};
  void toastPosition;

  enqueueMuiToast(message, { ...rest, ...(anchorOrigin ? { anchorOrigin } : null) });
};

const toastSuccess = (message: string, options?: ToastOptions) => toast(message, { ...options, variant: "success" });
const toastError = (message: string, options?: ToastOptions) => toast(message, { ...options, variant: "error" });
const toastWarning = (message: string, options?: ToastOptions) => toast(message, { ...options, variant: "warning" });
const toastInfo = (message: string, options?: ToastOptions) => toast(message, { ...options, variant: "info" });
const toastAction = (message: string, options?: ToastOptions) => toast(message, { ...options, variant: "action" });

export { toast, toastSuccess, toastError, toastWarning, toastInfo, toastAction };
export type { ToastOptions, ToastPosition, MuiToastAction as ToastAction };
